import _ from "lodash";
import Promise from "bluebird";
import L from "lodash/fp";
import { purep, reject } from "bluebird-promisell";

export const propEq = L.curry((prop, value, obj) => !L.isNil(obj) && !L.isEmpty(obj) && obj[prop] === value);

export const equals = L.curry((one, two) => one === two);

export const not = a => !a;

export const complement = f => L.compose(not, f);

//TODO: replace with a general invoke ala Ramda
export const invokeGetter = L.curry((get, obj) => obj[get]());

//TODO: rename tap
export const pass = f => a => {
  f(a);
  return a;
};

export const isInstance = L.curry((clazz, o) => o instanceof clazz);

export const when = L.curry((predicate, f, x) => (predicate(x) ? f(x) : x));
export const unless = L.curry((predicate, f, x) => (predicate(x) ? x : f(x)));
export const ifElse = L.curry((predicate, yes, no, x) => (predicate(x) ? yes(x) : no(x)));

// promise stuff below... (eventually move these into a separate helper)

export const catchIfP = L.curry((predicate, f, error) => (predicate(error) ? f(error) : reject(error)));

// passP :: (a -> Promise a) -> a -> Promise a
export const passP = f => a => f(a).then(() => a);

export const waitForP = p => a => p.then(() => a);

export const delayP = L.curry(
  (delay, a) =>
    new Promise(resolve => {
      setTimeout(() => {
        resolve(a);
      }, delay);
    }),
);

// filterP :: predicate -> Promise -> Promise
export const filterP = L.curry((predicate, p) => p.then(v => (predicate(v) ? v : null)));

export const nullP = () => purep(null);

export const andThenP = L.curry((f, p) => p.then(f));

export const isBlank = value => value === undefined || L.isNil(value) || L.isEmpty(value);

/**
 * Whether nothing in particular holds focus, which is the state the document is left in when
 * the focused element is hidden or removed -- as happens whenever a control is swapped for
 * its counterpart. Use it to guard focus restoration, so it only reclaims focus that was
 * orphaned rather than taking it from somewhere the user deliberately put it.
 *
 * Browsers disagree on whether the fallback is <body> or null, so both count.
 *
 * @returns {Boolean}
 */
export const focusIsOrphaned = () => {
  const focused = document.activeElement;

  return L.isNil(focused) || focused === document.body;
};

/**
 * Smallest shift along one axis that brings [itemStart, itemStart + itemSize] inside
 * [viewStart, viewStart + viewSize], minus a margin at each end.
 *
 * @returns {Number} 0 when it already fits
 */
const axisDeltaToReveal = (viewStart, viewSize, itemStart, itemSize, margin) => {
  const itemEnd = itemStart + itemSize;

  // A margin at each end can't eat the whole viewport; fall back to no margin if it would.
  const inset = viewSize - margin * 2 > 0 ? margin : 0;
  const windowStart = viewStart + inset;
  const windowSize = viewSize - inset * 2;

  // Too big to reveal in full, so no delta would satisfy the tests below. Center it instead --
  // that shows as much of it as there is room for.
  if (itemSize >= windowSize) return itemStart + itemSize / 2 - (viewStart + viewSize / 2);

  if (itemStart < windowStart) return itemStart - windowStart;
  if (itemEnd > windowStart + windowSize) return itemEnd - (windowStart + windowSize);

  return 0;
};

/**
 * Minimal pan that brings `item` fully inside `view`, without changing zoom. Pure so the
 * geometry can be tested without OpenSeadragon -- both arguments are read as plain
 * {x, y, width, height}, which an OpenSeadragon.Rect satisfies.
 *
 * @param {Object} view viewport bounds, in viewport coordinates
 * @param {Object} item the image's bounds, in viewport coordinates
 * @param {Number} [marginRatio] inset to leave at each edge, as a fraction of the viewport
 * @returns {{x: Number, y: Number}} deltas in viewport coordinates; both 0 when already visible
 */
export const panDeltaToReveal = (view, item, marginRatio = 0) => ({
  x: axisDeltaToReveal(view.x, view.width, item.x, item.width, view.width * marginRatio),
  y: axisDeltaToReveal(view.y, view.height, item.y, item.height, view.height * marginRatio),
});

export const getObjectDiff = (obj1, obj2) => {
  const diff = Object.keys(obj1).reduce((result, key) => {
    if (!obj2.hasOwnProperty(key)) {
      result.push(key);
    } else if (_.isEqual(obj1[key], obj2[key])) {
      const resultKeyIndex = result.indexOf(key);
      result.splice(resultKeyIndex, 1);
    }
    return result;
  }, Object.keys(obj2));

  return diff;
};
